import { BILLING_COMMAND_ERROR_MESSAGES } from "@intelligems/ig-types/dtos/billing/commands/catalog";
import { MODULAR_PLAN_NAME } from "@intelligems/ig-types/dtos/billing/modular-catalog";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useSettleShopifyApprovalReturn } from "@src/api/billing-workflow/shopify-approval/use-settle-shopify-approval-return";
import { useGetBillingPortal } from "@src/api/stripe/get-billing-portal";
import {
  orderVolumeTier,
  SMART_PRICING_VARIANTS,
} from "@src/constants/billing";
import { organizationsKeys } from "@src/api/queryKeys";
import { useWebsocket } from "@src/hooks/websocket";
import { setConfetti } from "@src/store/slices/global";
import {
  clearShopifyApprovalReturnInFlight,
  markShopifyApprovalReturnInFlight,
} from "@src/utils/shopify-approval-return";
import { useQueryClient } from "@tanstack/react-query";
import {
  mpBillingCheckoutErrorShown,
  mpBillingModuleRemoved,
  mpBillingPageViewed,
  mpBillingPlanCancelConfirmed,
  mpBillingPlanChangeCompleted,
  mpBillingPlanDowngradeStarted,
  mpBillingShopifyApprovalReturned,
  mpBillingShopifyApprovalStarted,
} from "@src/utils/mixpanel";
import type {
  BillingCommandViewModel,
  BillingDialogConfirmationIntent,
  BillingEntityActionIntent,
  BillingSelectionIntent,
  BillingSettingsWorkflow,
  BillingWorkflowDialogViewModel,
} from "./types";
import {
  buildBillingCatalog,
  entityFor,
  subscriptionPeriodEndFor,
} from "./internal/catalog";
import type {
  BillingCommandAnalyticsEvent,
  BillingCommandFeedback,
  BillingCommandPlan,
} from "./internal/commandTypes";
import { planBillingCommand } from "./internal/commandPlanner";
import {
  APPROVAL_RETURN_POLL_CAP_MS,
  approvalReturnFeedback,
  readApprovalReturnArrival,
  readApprovalReturnOutcome,
  readChargeIdMarker,
} from "./internal/approvalReturn";
import {
  type EntityActionDialogInput,
  projectEntityActionDialog,
} from "./internal/dialogProjection";
import {
  projectBillingPage,
  projectPlanChangeDialog,
} from "./internal/pageProjection";
import {
  createInitialSelectionFromDesiredPlan,
  desiredPlanCadence,
  transitionSelection,
} from "./internal/selection";
import type { BillingCatalog, BillingSnapshot } from "./internal/types";
import { useBillingCommandExecutor } from "./internal/useBillingCommandExecutor";
import { useBillingWorkflowSnapshot } from "./internal/useBillingWorkflowSnapshot";
import {
  type ApprovalReturnOutcome,
  initialBillingWorkflowState,
  selectWorkflowSnapshot,
  workflowReducer,
} from "./internal/workflowReducer";

const nowSeconds = (): number => Math.floor(Date.now() / 1_000);

// Terminal failures that a toast can't resolve: the merchant needs a recovery
// route, so they escalate to a dialog instead.
const ESCALATED_ERROR_CODES: ReadonlySet<string> = new Set([
  "bridge_only_trial_plan_not_chosen",
  "shopify_app_subscription_not_active",
]);

const emitAnalytics = (event: BillingCommandAnalyticsEvent): void => {
  switch (event.type) {
    case "billing-plan-change-completed":
      mpBillingPlanChangeCompleted({
        billingCycle: event.billingCycle,
        changeType: event.changeType,
        modules: [...event.moduleIds],
        modulesCount: event.moduleIds.length,
        planName: event.planId ?? MODULAR_PLAN_NAME,
        totalAmount: event.totalAmount,
      });
      return;
    case "billing-shopify-approval-started":
      mpBillingShopifyApprovalStarted({
        source: "plan_change",
        billingCycle: event.billingCycle,
        changeType: event.changeType,
        modules: event.moduleIds,
        planName: event.planId ?? MODULAR_PLAN_NAME,
      });
      return;
    case "billing-plan-downgrade-started":
      mpBillingPlanDowngradeStarted({
        activeModules: [...event.activeModuleIds],
        ...(event.currentPlanId ? { currentPlan: event.currentPlanId } : {}),
      });
      return;
    case "billing-module-removed":
      mpBillingModuleRemoved({
        moduleName: event.moduleName,
        remainingModules: [...event.remainingModuleIds],
        remainingModulesCount: event.remainingModuleIds.length,
      });
      return;
    case "billing-plan-cancel-confirmed":
      mpBillingPlanCancelConfirmed({
        activeModules: [...event.activeModuleIds],
        cancellationReason: event.cancellationReason,
        currentPlan: event.currentPlanId,
      });
      return;
    case "billing-checkout-error-shown":
      mpBillingCheckoutErrorShown({
        errorMessage: event.errorMessage,
        errorType: event.errorType,
        step: event.step,
      });
      return;
    default: {
      // A planned event with no mapping would vanish silently, so a new
      // variant has to fail the build here instead.
      const unmapped: never = event;
      return unmapped;
    }
  }
};

const trialModuleIds = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog
): string[] => {
  const trialPriceIds = new Set(
    snapshot.subscriptionAccessData.subscriptions.historical
      .filter(({ status }) => status === "trialing")
      .flatMap(({ priceIds }) => priceIds)
  );

  return catalog.modules
    .filter((module) => {
      const keys = new Set([module.id, ...(module.variantProductKeys ?? [])]);
      return snapshot.subscriptionAccessData.access.products.some(
        ({ entitlementProfileKey, priceIds }) =>
          entitlementProfileKey !== null &&
          keys.has(entitlementProfileKey) &&
          priceIds.some((priceId) => trialPriceIds.has(priceId))
      );
    })
    .map(({ id }) => id);
};

const toPublicActionDialog = (
  dialog: ReturnType<typeof projectEntityActionDialog>
): BillingWorkflowDialogViewModel => {
  if (!dialog) {
    return { type: "closed" };
  }
  const { kind, ...model } = dialog;
  return { ...model, type: kind } as BillingWorkflowDialogViewModel;
};

export const useBillingSettingsWorkflow = (): BillingSettingsWorkflow => {
  const [state, dispatch] = useReducer(
    workflowReducer,
    initialBillingWorkflowState
  );
  const source = useBillingWorkflowSnapshot({
    enabled: true,
    pollForApprovalReturn:
      state.approvalReturn.status === "waiting" &&
      state.approvalReturn.pollsSnapshot,
  });
  const websocket = useWebsocket();
  const portal = useGetBillingPortal(false);
  const appDispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const viewedAccountStateRef = useRef<string | null>(null);
  const reducerOrgIdRef = useRef(source.orgId);

  const feedback = useCallback(
    (value: BillingCommandFeedback) => {
      if (value.tone === "error") {
        toast.error(value.message);
      } else if (value.tone === "success") {
        toast.success(value.message);
      } else {
        toast.info(value.message);
      }
      if (value.confetti) {
        appDispatch(setConfetti(true));
      }
    },
    [appDispatch]
  );

  const redirect = useCallback((url: string) => {
    window.location.assign(url);
  }, []);

  const projectedSnapshot = useMemo(
    () =>
      source.rawSnapshot
        ? selectWorkflowSnapshot(source.rawSnapshot, state, source.orgId)
        : null,
    [source.rawSnapshot, state]
  );

  const pageCatalog = useMemo(() => {
    if (!projectedSnapshot || !source.catalog) {
      return null;
    }

    const catalogs = {
      month: buildBillingCatalog(projectedSnapshot, "month"),
      year: buildBillingCatalog(projectedSnapshot, "year"),
    };
    const cadence =
      desiredPlanCadence(
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        catalogs,
        projectedSnapshot.livePrices
      ) ?? source.catalog.cadence;

    return catalogs[cadence];
  }, [projectedSnapshot, source.catalog]);

  const upgradeTrial = useCallback((): boolean => {
    if (!pageCatalog || !projectedSnapshot) {
      return false;
    }
    dispatch({
      type: "open-plan-change",
      selection: createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {
          preselectedModuleIds: trialModuleIds(projectedSnapshot, pageCatalog),
        },
        projectedSnapshot.livePrices
      ),
    });
    return true;
  }, [pageCatalog, projectedSnapshot]);

  const onTerminalFailure = (errorCode: string): boolean => {
    if (!ESCALATED_ERROR_CODES.has(errorCode)) {
      return false;
    }
    if (errorCode === "bridge_only_trial_plan_not_chosen") {
      const opened = upgradeTrial();
      if (opened) {
        feedback({
          message:
            BILLING_COMMAND_ERROR_MESSAGES.bridge_only_trial_plan_not_chosen,
          tone: "info",
          confetti: false,
        });
      }
      return opened;
    }
    dispatch({
      type: "show-command-blocked",
      title: "Your Shopify subscription is no longer active",
      body: "This plan can't be resumed because its Shopify subscription was already cancelled. Start a new plan to continue using your modules.",
      acknowledgeLabel: "Got it",
      recovery: "start-new-plan",
    });
    return true;
  };

  const executor = useBillingCommandExecutor({
    command: state.command,
    dispatch,
    emitAnalytics,
    emitFeedback: feedback,
    invalidateSubscriptionAccess: source.invalidateSubscriptionAccess,
    isRawSnapshotFetching: source.isRawSnapshotFetching,
    onTerminalFailure,
    orgId: source.orgId,
    rawSnapshot: source.rawSnapshot,
    redirect,
    websocketMessage: websocket.lastJsonMessage,
  });

  const handlePlan = useCallback(
    (plan: BillingCommandPlan): boolean => {
      if (plan.kind === "planned") {
        void executor.execute(plan);
        return true;
      }
      if (plan.kind === "no-op") {
        feedback(plan.feedback);
        return false;
      }
      for (const event of plan.analytics) {
        emitAnalytics(event);
      }
      if (plan.feedback.tone === "dialog") {
        dispatch({
          type: "show-command-blocked",
          acknowledgeLabel: plan.feedback.acknowledgeLabel,
          body: plan.feedback.body,
          title: plan.feedback.title,
        });
      } else {
        feedback(plan.feedback);
      }
      return false;
    },
    [executor, feedback]
  );

  const runIntent = useCallback(
    (intent: Parameters<typeof planBillingCommand>[0]["intent"]): boolean => {
      if (!source.rawSnapshot) {
        return false;
      }
      return handlePlan(
        planBillingCommand({
          intent,
          nowSec: nowSeconds(),
          snapshot: source.rawSnapshot,
          stagedCouponId: state.stagedCoupon?.id ?? null,
        })
      );
    },
    [handlePlan, source.rawSnapshot, state.stagedCoupon]
  );

  const openPlanChange = useCallback(() => {
    if (!pageCatalog || !projectedSnapshot) {
      return;
    }
    dispatch({
      type: "open-plan-change",
      selection: createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {},
        projectedSnapshot.livePrices
      ),
    });
  }, [pageCatalog, projectedSnapshot]);

  const preselectPlanChange = useCallback(
    (entityIds: readonly string[]) => {
      if (!pageCatalog || !projectedSnapshot) {
        return;
      }
      const entities = entityIds
        .map((id) => entityFor(pageCatalog, id))
        .filter((entity): entity is NonNullable<typeof entity> =>
          Boolean(entity)
        );
      if (entities.length === 0) {
        return;
      }

      const planEntity = entities.find((entity) => entity.type === "plan");
      const moduleIds = entities
        .filter((entity) => entity.type === "module")
        .map((entity) => entity.id);

      const selection = createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {
          ...(planEntity ? { preselectedPlanId: planEntity.id } : {}),
          ...(moduleIds.length > 0 ? { preselectedModuleIds: moduleIds } : {}),
        },
        projectedSnapshot.livePrices
      );
      // Smart Pricing adds open the kickoff before module selection, carrying
      // the preselection into the billing dialog's next step.
      const routeThroughKickoff = entities.some((entity) =>
        SMART_PRICING_VARIANTS.has(entity.id)
      );
      dispatch({
        type: routeThroughKickoff
          ? "preselect-smart-pricing-kickoff"
          : "preselect-plan-change",
        selection,
      });
    },
    [pageCatalog, projectedSnapshot]
  );

  const updateSelection = useCallback(
    (intent: BillingSelectionIntent) => {
      if (state.dialog.type !== "plan-change" || !projectedSnapshot) {
        return;
      }
      const cadence =
        intent.type === "change_cadence"
          ? intent.cadence === "annual"
            ? "year"
            : "month"
          : state.dialog.selection.cadence;
      const catalog = buildBillingCatalog(projectedSnapshot, cadence);
      dispatch({
        type: "update-selection",
        selection: transitionSelection(state.dialog.selection, intent, catalog),
      });
    },
    [projectedSnapshot, state.dialog]
  );

  const submit = useCallback(() => {
    if (state.dialog.type !== "plan-change") {
      return false;
    }

    return runIntent({
      type: "submit-selection",
      selection: state.dialog.selection,
    });
  }, [runIntent, state.dialog]);

  const applyCoupon = useCallback(
    (
      entityId: string,
      couponId: string,
      couponLabel: string,
      percentOff?: number,
      amountOff?: number
    ) =>
      runIntent({
        type: "apply-coupon",
        entityId,
        couponId,
        couponLabel,
        percentOff,
        amountOff,
      }),
    [runIntent]
  );

  const stageCoupon = useCallback(
    (
      coupon: Readonly<{
        id: string;
        percentOff?: number;
        amountOff?: number;
      }> | null
    ) => {
      dispatch({ type: "stage-coupon", coupon });
    },
    []
  );

  const openSmartPricingSelection = useCallback(() => {
    if (state.dialog.type !== "smart-pricing-kickoff") {
      return;
    }
    dispatch({
      type: "preselect-plan-change",
      selection: state.dialog.selection,
    });
  }, [state.dialog]);

  const requestEntityAction = useCallback(
    (intent: BillingEntityActionIntent) => {
      if (!source.rawSnapshot || !pageCatalog) {
        return;
      }
      if (intent.type === "choose-plan") {
        upgradeTrial();
        return;
      }
      if (
        intent.type === "release" ||
        intent.type === "cancel-pending-removal" ||
        intent.type === "cancel-pending-pause"
      ) {
        runIntent(intent);
        return;
      }

      const entity = entityFor(pageCatalog, intent.entityId);
      if (!entity) {
        return;
      }
      if (
        intent.type === "remove-module" ||
        intent.type === "cancel-trial" ||
        intent.type === "pause"
      ) {
        dispatch({ type: "request-entity-action", action: intent });
        return;
      }

      // The core module can't be cancelled on its own, so the command this
      // flow eventually runs targets the plan, not the module.
      if (intent.type === "cancel-core-module") {
        dispatch({
          type: "open-core-module-cancel",
          entityId: intent.entityId,
          expectedCancellationAt: subscriptionPeriodEndFor(
            source.rawSnapshot,
            pageCatalog,
            intent.entityId,
            nowSeconds()
          ),
          planEntityId: pageCatalog.currentPlan?.id ?? intent.entityId,
        });
        return;
      }

      const model = projectEntityActionDialog({
        catalog: pageCatalog,
        commitmentStartAt: entity.startDate,
        entity,
        kind: "cancel",
        nowSec: nowSeconds(),
        snapshot: source.rawSnapshot,
        timing: intent.timing,
      });
      dispatch({
        type: "request-entity-action",
        action: {
          type: "cancel",
          entityId: entity.id,
          expectedCancellationAt:
            model?.kind === "cancel" && model.effectiveAt !== null
              ? model.effectiveAt
              : subscriptionPeriodEndFor(
                  source.rawSnapshot,
                  pageCatalog,
                  entity.id,
                  nowSeconds()
                ),
          timing: intent.timing,
        },
      });
    },
    [pageCatalog, runIntent, source.rawSnapshot, upgradeTrial]
  );

  const confirmDialog = useCallback(
    (intent: BillingDialogConfirmationIntent) => {
      if (
        state.dialog.type !== intent.type ||
        state.dialog.entityId !== intent.entityId
      ) {
        return;
      }
      switch (state.dialog.type) {
        case "cancel": {
          if (intent.type !== "cancel" || !pageCatalog || !projectedSnapshot) {
            return;
          }
          const entity = entityFor(pageCatalog, state.dialog.entityId);
          const model = projectEntityActionDialog({
            catalog: pageCatalog,
            commitmentStartAt: entity?.startDate,
            entity,
            kind: "cancel",
            nowSec: nowSeconds(),
            snapshot: projectedSnapshot,
            timing: state.dialog.timing,
          });
          if (model?.kind !== "cancel" || model.disabledReason) {
            return;
          }
          runIntent({
            type: "cancel",
            entityId: state.dialog.entityId,
            expectedCancellationAt: state.dialog.expectedCancellationAt,
            reasons: intent.reasons,
            timing: state.dialog.timing,
          });
          return;
        }
        case "pause": {
          if (!pageCatalog || !projectedSnapshot) {
            return;
          }
          const model = projectEntityActionDialog({
            catalog: pageCatalog,
            entityId: state.dialog.entityId,
            kind: "pause",
            nowSec: nowSeconds(),
            snapshot: projectedSnapshot,
          });
          if (model?.kind !== "pause" || model.effectiveAt === null) {
            return;
          }
          runIntent({
            type: "pause",
            entityId: state.dialog.entityId,
            expectedPauseAt: model.effectiveAt,
          });
          return;
        }
        case "cancel-core-module": {
          if (
            intent.type !== "cancel-core-module" ||
            state.dialog.step !== "confirm"
          ) {
            return;
          }
          runIntent({
            type: "cancel",
            entityId: state.dialog.planEntityId,
            expectedCancellationAt: state.dialog.expectedCancellationAt,
            reasons: intent.reasons,
            timing: "immediate",
          });
          return;
        }
        case "remove-module":
          runIntent({
            type: "remove-module",
            entityId: state.dialog.entityId,
          });
          return;
        case "cancel-trial":
          runIntent({
            type: "cancel-trial",
            entityId: state.dialog.entityId,
          });
      }
    },
    [pageCatalog, projectedSnapshot, runIntent, state.dialog]
  );

  const manage = useCallback(() => {
    if (source.management.type === "shopify") {
      redirect(source.management.url);
      return;
    }
    if (source.management.type !== "stripe" || !source.stripeSubscriptionId) {
      return;
    }
    portal.mutate(
      {
        body: { stripeSubscriptionId: source.stripeSubscriptionId },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        onError: () => {
          mpBillingCheckoutErrorShown({
            errorMessage: "Failed to retrieve billing portal URL",
            errorType: "portal_redirect_failed",
            step: "confirmation",
          });
          toast.error("Failed to open the billing portal. Please try again.");
        },
      }
    );
  }, [portal, redirect, source.management, source.stripeSubscriptionId]);

  const page = useMemo(
    () =>
      projectedSnapshot && pageCatalog && source.status === "ready"
        ? projectBillingPage({
            catalog: pageCatalog,
            management: source.management,
            ordersPerMonth: source.ordersPerMonth,
            snapshot: projectedSnapshot,
            stagedCoupon: state.stagedCoupon,
          })
        : null,
    [
      pageCatalog,
      projectedSnapshot,
      source.management,
      source.ordersPerMonth,
      source.status,
      state.stagedCoupon,
    ]
  );

  const command = useMemo<BillingCommandViewModel>(() => {
    const { command: stateCommand } = state;
    const { status } = stateCommand;

    if (status === "idle") {
      return {
        awaitsNamedShopifyApproval: false,
        awaitsProviderApproval: false,
        status,
      };
    }

    const { awaitsNamedShopifyApproval, plan } = stateCommand;
    const { mayAwaitShopifyApproval } = plan;

    return {
      awaitsNamedShopifyApproval,
      awaitsProviderApproval: mayAwaitShopifyApproval,
      status,
    };
  }, [state.command]);

  const dialog = useMemo<BillingWorkflowDialogViewModel>(() => {
    if (state.dialog.type === "closed") {
      return { type: "closed" };
    }
    if (state.dialog.type === "command-blocked") {
      return state.dialog;
    }
    if (state.dialog.type === "smart-pricing-kickoff") {
      return { type: "smart-pricing-kickoff" };
    }
    if (state.dialog.type === "smart-pricing-whats-next") {
      return {
        type: "smart-pricing-whats-next",
        orderVolumeTier: state.dialog.orderVolumeTier,
        tierPurchased: state.dialog.tierPurchased,
      };
    }
    if (!projectedSnapshot || !pageCatalog) {
      return { type: "closed" };
    }
    if (state.dialog.type === "plan-change") {
      const catalog = buildBillingCatalog(
        projectedSnapshot,
        state.dialog.selection.cadence
      );
      return projectPlanChangeDialog({
        catalog,
        nowSec: nowSeconds(),
        ordersPerMonth: source.ordersPerMonth,
        selection: state.dialog.selection,
        snapshot: projectedSnapshot,
        stagedCoupon: state.stagedCoupon,
      });
    }

    let input: EntityActionDialogInput;
    if (state.dialog.type === "remove-module") {
      input = {
        catalog: pageCatalog,
        kind: "remove-module",
        moduleId: state.dialog.entityId,
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
      };
    } else if (state.dialog.type === "cancel-trial") {
      input = {
        catalog: pageCatalog,
        kind: "cancel-trial",
        moduleId: state.dialog.entityId,
      };
    } else if (state.dialog.type === "cancel-core-module") {
      input = {
        catalog: pageCatalog,
        kind: "cancel-core-module",
        moduleId: state.dialog.entityId,
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
        step: state.dialog.step,
      };
    } else if (state.dialog.type === "pause") {
      input = {
        catalog: pageCatalog,
        entityId: state.dialog.entityId,
        kind: "pause",
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
      };
    } else {
      const entity = entityFor(pageCatalog, state.dialog.entityId);
      input = {
        catalog: pageCatalog,
        commitmentStartAt: entity?.startDate,
        entity,
        kind: "cancel",
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
        timing: state.dialog.timing,
      };
    }
    return toPublicActionDialog(projectEntityActionDialog(input));
  }, [
    pageCatalog,
    projectedSnapshot,
    source.ordersPerMonth,
    state.dialog,
    state.stagedCoupon,
  ]);

  useEffect(() => {
    if (reducerOrgIdRef.current === source.orgId) {
      return;
    }
    reducerOrgIdRef.current = source.orgId;
    dispatch({ type: "organization-changed" });
  }, [source.orgId]);

  useEffect(() => {
    if (!page || viewedAccountStateRef.current === page.accountState) {
      return;
    }
    viewedAccountStateRef.current = page.accountState;
    mpBillingPageViewed(page.accountState);
  }, [page]);

  // `preselectPlanChange` and `upgradeTrial` dispatch, and their identity is
  // derived from the reducer state through `projectedSnapshot`, so opening the
  // plan change re-runs this effect. The navigate that strips the marker lands
  // as a transition, so those re-runs still read the marker off the location,
  // and the dispatch and the replace repeat until React gives up with "Maximum
  // update depth exceeded" and the browser rate-limits `replaceState`. The ref
  // records consumption at once, the way the `charge_id` marker below is
  // consumed, and re-arms once the strip commits so a later arrival still
  // applies.
  const consumedPlanLinkRef = useRef<string | null>(null);
  const selectedParam = searchParams.get("selected");
  const choosePlanRequested = searchParams.has("choosePlan");
  const planLink = searchParams.has("selected")
    ? `selected=${selectedParam}`
    : choosePlanRequested
      ? "choosePlan"
      : null;
  useEffect(() => {
    if (source.status !== "ready") {
      return;
    }
    if (planLink === null) {
      consumedPlanLinkRef.current = null;
      return;
    }
    if (consumedPlanLinkRef.current === planLink) {
      return;
    }
    consumedPlanLinkRef.current = planLink;
    const ids = selectedParam?.split(",").filter(Boolean) ?? [];
    if (ids.length > 0) {
      preselectPlanChange(ids);
    } else if (choosePlanRequested) {
      upgradeTrial();
    }
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    next.delete("choosePlan");
    const search = next.toString();
    navigate(
      { hash, pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );
  }, [
    choosePlanRequested,
    hash,
    navigate,
    pathname,
    planLink,
    preselectPlanChange,
    searchParams,
    selectedParam,
    source.status,
    upgradeTrial,
  ]);

  const approvalReturnRef = useRef(state.approvalReturn);
  approvalReturnRef.current = state.approvalReturn;
  // The settle reply lands after renders the arming snapshot has not seen, and
  // its copy depends on the commitment's approval state as it stands now.
  const latestRawSnapshotRef = useRef(source.rawSnapshot);
  latestRawSnapshotRef.current = source.rawSnapshot;
  const settleApprovalReturn = useCallback(
    (outcome: ApprovalReturnOutcome, snapshot: BillingSnapshot) => {
      dispatch({ type: "approval-return-settled", outcome });
      const message = approvalReturnFeedback(outcome, snapshot);
      if (message) {
        feedback(message);
      }
    },
    [feedback]
  );

  // The settle endpoint reads Shopify, so its reply usually ends the wait. A
  // pending approval, one the read model has yet to show, or a 404 for a
  // charge the endpoint cannot match yet falls back to polling.
  const { mutate: requestApprovalSettle } = useSettleShopifyApprovalReturn();
  const invalidateSubscriptionAccess = source.invalidateSubscriptionAccess;
  const settleReturnedCharge = useCallback(
    (chargeId: string, armedSnapshot: BillingSnapshot) => {
      const isStillWaiting = () =>
        approvalReturnRef.current.status === "waiting";
      const pollForTheRestOfTheWindow = () => {
        if (!isStillWaiting()) {
          return;
        }
        dispatch({ type: "approval-return-polling" });
      };
      requestApprovalSettle(
        { body: { chargeId }, pathParams: undefined, queryParams: undefined },
        {
          onSuccess: ({ outcome }) => {
            if (!isStillWaiting()) {
              return;
            }
            if (outcome !== "pending") {
              invalidateSubscriptionAccess();
            }
            if (outcome === "declined" || outcome === "expired") {
              settleApprovalReturn(
                outcome,
                latestRawSnapshotRef.current ?? armedSnapshot
              );
              return;
            }
            pollForTheRestOfTheWindow();
          },
          onError: pollForTheRestOfTheWindow,
        }
      );
    },
    [invalidateSubscriptionAccess, requestApprovalSettle, settleApprovalReturn]
  );

  // The navigate that strips the marker lands as a transition, so later renders
  // can still read it from the location. The ref records consumption at once.
  const consumedChargeIdRef = useRef<string | null>(null);

  // Arrival from a Blocking Approval. The `charge_id` marker is consumed here so
  // a mid-wait refresh lands on a plain billing page instead of a re-armed one.
  useEffect(() => {
    if (source.status !== "ready" || !source.rawSnapshot) {
      return;
    }
    const marker = readChargeIdMarker({ hash, search });
    if (!marker) {
      return;
    }

    const arrival = readApprovalReturnArrival(source.rawSnapshot);
    if (arrival.kind === "ignored") {
      return;
    }
    if (consumedChargeIdRef.current === marker.chargeId) {
      return;
    }
    consumedChargeIdRef.current = marker.chargeId;

    mpBillingShopifyApprovalReturned({
      alreadyApplied: arrival.alreadyApplied,
      approvalKind: arrival.approvalKind,
    });
    const armedAt = Date.now();
    markShopifyApprovalReturnInFlight({
      orgId: source.orgId,
      expiresAt: armedAt + APPROVAL_RETURN_POLL_CAP_MS,
    });
    dispatch({
      type: "approval-return-armed",
      approvalKind: arrival.approvalKind,
      armedAt,
    });
    approvalReturnRef.current = {
      status: "waiting",
      approvalKind: arrival.approvalKind,
      armedAt,
      pollsSnapshot: false,
    };
    settleReturnedCharge(marker.chargeId, source.rawSnapshot);
    navigate(
      { hash: marker.hash, pathname, search: marker.search },
      { replace: true }
    );
  }, [
    hash,
    navigate,
    pathname,
    search,
    settleReturnedCharge,
    source.orgId,
    source.rawSnapshot,
    source.status,
  ]);

  // The celebration's truth is `planIntent.state === "applied"`, never Shopify's
  // `approved` and never a completed command. Settling is what makes it fire
  // once: a later refetch that re-reports `applied` finds the wait already over.
  useEffect(() => {
    if (state.approvalReturn.status !== "waiting" || !source.rawSnapshot) {
      return;
    }
    const outcome = readApprovalReturnOutcome(source.rawSnapshot);
    if (outcome === null) {
      return;
    }

    settleApprovalReturn(outcome, source.rawSnapshot);
  }, [settleApprovalReturn, source.rawSnapshot, state.approvalReturn]);

  // The billing nudge reads the org summary, not this page's snapshot, so it
  // is refreshed once the wait ends to drop a card the return has overtaken.
  const queryClient = useQueryClient();
  const approvalReturnStatus = state.approvalReturn.status;
  useEffect(() => {
    if (approvalReturnStatus !== "settled") {
      return;
    }
    clearShopifyApprovalReturnInFlight();
    void queryClient.invalidateQueries({
      queryKey: organizationsKeys.orgSummary(source.orgId),
    });
  }, [approvalReturnStatus, queryClient, source.orgId]);

  useEffect(() => {
    const { approvalReturn } = state;
    if (approvalReturn.status !== "waiting") {
      return;
    }
    const timer = window.setTimeout(
      () => dispatch({ type: "approval-return-settled", outcome: "timed_out" }),
      Math.max(
        0,
        approvalReturn.armedAt + APPROVAL_RETURN_POLL_CAP_MS - Date.now()
      )
    );
    return () => window.clearTimeout(timer);
  }, [state.approvalReturn]);

  return {
    actions: {
      acknowledgeBlocked: () => dispatch({ type: "acknowledge-blocked" }),
      advanceCoreModuleCancel: () =>
        dispatch({ type: "advance-core-module-cancel" }),
      applyCoupon,
      cancelSmartPricingKickoff: () =>
        dispatch({ type: "cancel-smart-pricing-kickoff" }),
      confirmDialog,
      openSmartPricingSelection,
      dismissDialog: () => dispatch({ type: "dismiss-dialog" }),
      manage,
      openPlanChange,
      openSmartPricingKickoff: () =>
        dispatch({ type: "open-smart-pricing-kickoff" }),
      preselectPlanChange,
      requestEntityAction,
      retry: source.refetch,
      showSmartPricingWhatsNext: (tierPurchased) =>
        dispatch({
          type: "show-smart-pricing-whats-next",
          orderVolumeTier: orderVolumeTier(source.ordersPerMonth),
          tierPurchased,
        }),
      stageCoupon,
      submit,
      updateSelection,
      upgradeTrial,
    },
    awaitsApprovalReturn: state.approvalReturn.status === "waiting",
    command,
    dialog,
    failureReason: source.failureReason,
    ordersPerMonth: source.ordersPerMonth,
    page,
    stagedCoupon: state.stagedCoupon,
    status: source.status === "error" ? "error" : page ? "ready" : "loading",
  };
};
